export type FilterSelectionPart = {
  name: string;
  value: string;
  /** Primary filters lead the chip row — they are what identifies the view. */
  isPrimary: boolean;
};

export type FilterSelectionSummary = {
  /**
   * Identity of the selection. Built from ids ONLY — never labels. Selection
   * labels resolve asynchronously (`isResolvingSelectionLabels`), so a
   * label-derived key would change under a selection that never actually
   * changed, splitting one batch of requests across two groups the moment a
   * reference tree finished loading.
   */
  signature: string;
  /**
   * One entry per active filter, ordered for display. Kept as parts rather than
   * a joined string so the drawer can lay them out as chips: a single line of
   * `A: x · B: y · C: z` truncates to nonsense at drawer width, which is
   * precisely the case where you need to tell two selections apart.
   */
  parts: FilterSelectionPart[];
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (
  object: Record<string, unknown>,
  key: string
): string | undefined => {
  const value = object[key];
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

/**
 * `CONTINGENT_WORKERS` → `Contingent workers`. Enum-shaped values reach the
 * filter map as raw wire constants, and shouting them at the reader is most of
 * what made the old single-line label unreadable.
 */
const humanise = (value: string) =>
  /^[A-Z][A-Z0-9_]*$/.test(value)
    ? value
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/^./, (character) => character.toUpperCase())
    : value;

/** Enough to identify a selection; the full text is in the row's tooltip. */
const MAX_VALUE_LENGTH = 40;

const truncate = (value: string) =>
  value.length <= MAX_VALUE_LENGTH
    ? value
    : `${value.slice(0, MAX_VALUE_LENGTH - 1)}…`;

/**
 * Read generically rather than switching on `FilterType`.
 *
 * `Filter` is a twenty-member union, and a per-variant renderer would need a new
 * arm every time one is added — silently falling back to "No filters" until
 * someone noticed. Every variant expresses its value as one of three shapes, so
 * matching on the shape covers the ones that exist now and the ones that don't
 * yet.
 */
const describeFilterValue = (
  filter: Record<string, unknown>
): { ids: string; value: string } | undefined => {
  const selections = filter['selections'];
  if (Array.isArray(selections) && selections.length > 0) {
    const entries = selections.filter(isPlainObject);
    if (entries.length === 0) return undefined;
    const ids = entries.map((entry) => readString(entry, 'id') ?? '?');
    const labels = entries.map(
      (entry, index) =>
        readString(entry, 'name') ??
        readString(entry, 'label') ??
        ids[index] ??
        '?'
    );
    // "Google, Meta +3" beats a chip that spans the whole drawer.
    const shown = labels.slice(0, 2).map(humanise).join(', ');
    const rest = labels.length - Math.min(labels.length, 2);
    return {
      ids: ids.join(','),
      value: rest > 0 ? `${shown} +${rest}` : shown,
    };
  }

  const min = filter['min'];
  const max = filter['max'];
  if (min != null || max != null) {
    const range = `${String(min ?? '')}–${String(max ?? '')}`;
    return { ids: range, value: range };
  }

  const value = filter['value'];
  if (value != null && (typeof value !== 'object' || Array.isArray(value))) {
    const rendered = Array.isArray(value)
      ? value.map(String).join(',')
      : String(value);
    if (rendered.length === 0) return undefined;
    return { ids: rendered, value: humanise(rendered) };
  }

  return undefined;
};

/**
 * Collapses the effective filters into a stable key plus display parts, so every
 * request fired under one selection groups together.
 *
 * Takes `useEffectiveFilters` output, NOT the raw `activeFilterMap` — the map
 * carries every filter the page declares, sitting at its definition defaults,
 * and summarising it directly buries the real selection under a dozen untouched
 * screener ranges.
 *
 * The signature is computed from id-sorted entries: the incoming order follows
 * how filters were added or restored, which is not stable across a reload, and
 * an unsorted key would make the same selection look like a new one.
 * Display order is independent of that — primary filters lead, because "which
 * company am I looking at" is the question the heading has to answer first.
 */
export const summariseFilterSelection = (
  effectiveFilters: readonly unknown[]
): FilterSelectionSummary => {
  const entries: Array<{ signature: string; part: FilterSelectionPart }> = [];

  for (const filter of effectiveFilters) {
    if (!isPlainObject(filter)) continue;

    const filterId = readString(filter, 'id') ?? 'filter';
    const isPrimary = filter['isPrimary'] === true;

    // The caller has already reduced this to filters whose handler reports
    // them ACTIVE. What is left is the bar's display rule
    // (`useFilterTags`: `filter.isVisible && !filter.isPrimary`) — a filter can
    // apply without being worth a chip, e.g. the CONTINGENT_WORKERS default,
    // which ships `isActive: true, isVisible: false` and is commented in the
    // seed as "default, not visible as chip".
    //
    // The primary filter is exempt because the bar renders it separately, as
    // its own chip, rather than through the tag list.
    if (!isPrimary && filter['isVisible'] !== true) continue;

    // Multi-filters keep their values in `instances`; the wrapper itself has no
    // selections, so reading it directly would silently drop them.
    const instances = filter['instances'];
    const targets =
      Array.isArray(instances) && instances.length > 0
        ? instances.filter(isPlainObject)
        : [filter];

    targets.forEach((target, index) => {
      const described = describeFilterValue(target);
      if (!described) return;

      entries.push({
        signature: `${filterId}${index > 0 ? `#${index}` : ''}=${described.ids}`,
        part: {
          name:
            readString(target, 'name') ??
            readString(filter, 'name') ??
            filterId,
          value: truncate(described.value),
          isPrimary,
        },
      });
    });
  }

  const signature = [...entries]
    .sort((a, b) => a.signature.localeCompare(b.signature))
    .map((entry) => entry.signature)
    .join(';');

  const parts = [...entries]
    .sort((a, b) => {
      if (a.part.isPrimary !== b.part.isPrimary)
        return a.part.isPrimary ? -1 : 1;
      return a.part.name.localeCompare(b.part.name);
    })
    .map((entry) => entry.part);

  return { signature, parts };
};

/** Full, untruncated text for a tooltip. */
export const formatFilterSelection = (parts: FilterSelectionPart[]) =>
  parts.length === 0
    ? 'No filters'
    : parts.map((part) => `${part.name}: ${part.value}`).join(' · ');
